import { leadService } from "../services/leadService";
// Updated by Halimunnisa on 08-06-2026 to consolidate all lead-related handlers into a single hook for better organization and maintainability
import {
  showSuccessMessage,
  showErrorMessage,
  showApiError,
} from "@/lib/toast";
import { exportToExcel } from "@/utils/exportUtils";
import { LEAD_STAGES } from "@/constants/globalConstants";

export const useLeadsHandlers = ({
  selectedLead,
  setSelectedLead,
  setIsFormOpen,
  setPendingStatus,
  setIsAppointmentFormOpen,
  refetch,
  setIsExporting,
  queryParams,
  setFilters,
  setRevertRequest,
}) => {
  const handleLeadSubmit = async (data) => {
    try {
      if (selectedLead) {
        await leadService.update(selectedLead._id, data);
        showSuccessMessage("Lead updated successfully");
      } else {
        await leadService.create(data);
        showSuccessMessage("Lead added successfully");
      }
      refetch();
      setIsFormOpen(false);
      setSelectedLead(null);
    } catch (err) {
      showApiError(err);
    }
  };

  const handleUpdateLead = async (id, data) => {
    try {
      const response = await leadService.update(id, data);
      const emailStatus = response.data?.emailStatus || response.emailStatus;
//halimunnisa: 08-06-2026 - Updated lead update handler to provide more specific feedback based on email sending status after lead update, as per new backend response structure
      if (emailStatus === "sent") {
        showSuccessMessage("Lead updated & Quotation sent via email!");
      } else if (emailStatus === "invalid_address") {
        showErrorMessage(
          "Lead updated but Address not found! Please check the email address.",
        );
      } else if (emailStatus === "skipped") {
        showSuccessMessage(
          "Lead updated (Email skipped: invalid address). You can download it manually from the View modal.",
        );
      } else if (emailStatus === "failed") {
        showErrorMessage(
          "Lead updated but email delivery failed. Please download manually from the View modal.",
        );
      } else {
        showSuccessMessage("Lead updated");
      }
      refetch();
    } catch (err) {
      showApiError(err);
    }
  };

  const toggleLeadStatus = (lead, targetStatus) => {
    console.log("Lead", lead);
    console.log("leadStatus", lead.leadStatus);
    console.log("assignedTo", lead.assignedTo);
    console.log("salesExecutive", lead.salesExecutive);
    console.log("salesPerson", lead.salesPerson);

    if (lead.isLocked) {
      showErrorMessage("Lead status is locked");
      return;
    }

    const currentStatus = lead.leadStatus || lead.status || "new";
    const currentIndex = LEAD_STAGES.findIndex(s => s.key === currentStatus);
    const targetIndex = LEAD_STAGES.findIndex(s => s.key === targetStatus);

    if (currentIndex !== -1 && targetIndex !== -1 && targetIndex < currentIndex) {
      if (setRevertRequest) {
        setRevertRequest({ lead, targetStatus });
        return;
      }
    }

//halimunnisa: 08-06-2026 - Updated lead status toggle handler to route certain status changes through a pending status mechanism for better workflow control, as per new requirements
    const formStages = [
      "drop",
      "advance_payment_10",
      "advance_payment_50",
      "final_payment",
      "initial_design",
      "sign_of_copy",
      "prod_drawings",
    ];
    if (formStages.includes(targetStatus)) {
      setPendingStatus({ lead, targetStatus });
    } else {
      handleUpdateLead(lead._id, { status: targetStatus });
    }
  };

  const handleAppointmentSubmit = async (id, data) => {
    try {
      await leadService.update(id, data);
      showSuccessMessage("Appointment updated successfully");
      refetch();
      setIsAppointmentFormOpen(false);
      setSelectedLead(null);
    } catch (err) {
      showApiError(err);
    }
  };

  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const res = await leadService.getLeads({
        ...queryParams,
        limit: 10000,
        page: 1,
      });

      const data = res?.data || res;
      const exportLeads = data.leads || data.projects || data || [];
      //updated Halimunnisa :08-06-2026
      // Transform leads data into export format
      const exportData = exportLeads.map((l) => {
        let siteVisitDetails = "";
        if (l.siteVisitData && Array.isArray(l.siteVisitData.measurements)) {
          siteVisitDetails = l.siteVisitData.measurements
            .map((m, idx) => {
              let detail = `${m.room || "Room"} Measurements (${idx + 1})\n`;
              if (m.zone) detail += `Zone: ${m.zone}\n`;
              if (m.width || m.height || m.depth) {
                detail += `Dims: W: ${m.width || 0} x H: ${m.height || 0} x D: ${m.depth || 0}\n`;
              }
              if (m.qty) detail += `Qty: ${m.qty}\n`;
              if (m.notes) detail += `Note: ${m.notes}`;
              return detail.trim();
            })
            .join("\n\n");
        }
        //updated Halimunnisa :08-06-2026
        // Transform leads data into export format
        return {
          // 1. Basic & Contact Info
          "Verve ID": (l.customId || "").toUpperCase() || "—",
          "First Name": l.firstName || "",
          "Last Name": l.lastName || "",
          "Phone Number": l.phoneNumber || l.phone || "",
          "Email Address": l.email || "",

          // 2. Address Details
          "Address Line 1": l.addressLine1 || "",
          "Address Line 2": l.addressLine2 || "",
          "City": l.city || "",
          "District": l.district || "",
          "State": l.state || "",
          "PIN Code": l.pincode || "",
          "Office Location": l.officeLocation || "",

          // 3. Lead Source & Requirements
          "Reference Type": l.referenceType || "",
          "Referral Name / Details": l.referenceDetails || "",
          "Requirement Type": l.requirementType || "",
          "Type": l.propertyType || "",
          "Estimated Budget (₹)": l.budget || 0,
          "Source": l.source || "Manual",

          // 4. Assignments & General Status
          "Status": l.status?.replace(/_/g, ' ').toUpperCase() || "NEW",
          "Telecaller": l.telecaller?.name || l.telecallerId?.name || "—",
          "Assigned PM": l.projectManager?.name || l.projectManagerId?.name || "—",
          "Created At": l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN') : "—",

          // 5. Appointments & Site Visit
          "Appointment Date": l.appointmentDate ? new Date(l.appointmentDate).toLocaleString('en-IN') : "—",
          "Site Visit Measurements": siteVisitDetails || "—",
          "Floor Details": l.floorDetails || "—",
          "Power Consumption (Units)": l.existingPowerConsumption || "—",
          "Last Power Bill (₹)": l.existingPowerPaid || "—",
          // 6. Quotation & Approvals
          "Initial Quotation Date": (l.customerAcceptance?.quotationSnapshot?.quotationDate || (l.initialQuotationApproval?.isRequested ? l.updatedAt : null)) ? new Date(l.customerAcceptance?.quotationSnapshot?.quotationDate || l.updatedAt).toLocaleDateString('en-IN') : "—",
          "Initial Quotation Grand Total (₹)": l.quotationAmount || l.amount || 0,
          "Initial Quotation Approval Status": l.initialQuotationApproval?.approvalStatus?.replace(/_/g, ' ').toUpperCase() || "—",
          "IQ Design Approval Status": l.initialQuotationApproval?.designer?.status?.toUpperCase() || "—",
          "IQ PM Approval Status": l.initialQuotationApproval?.manager?.status?.toUpperCase() || "—",
          "IQ Owner Approval Status": l.initialQuotationApproval?.owner?.status?.toUpperCase() || "—",

          // 7. Customer Acceptance
          "Customer Acceptance Status": l.customerAcceptance?.status?.toUpperCase() || "—"
        };
      });

      exportToExcel(exportData, {
        filename: `Verve_Leads_Export_${new Date().toLocaleDateString().replace(/\//g, "-")}`,
      });
      showSuccessMessage("Export successful!");
    } catch (err) {
      showApiError(err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    setFilters({
      status: "all",
      activityStatus: "all",
      location: "all",
      source: "all",
      assignedTo: "all",
      projectManager: "all",
      date: "all",
      fromDate: null,
      toDate: null,
      sort: "updatedAt",
      order: "desc",
    });
  };

  return {
    handleLeadSubmit,
    handleUpdateLead,
    toggleLeadStatus,
    handleAppointmentSubmit,
    handleExport,
    handleReset,
  };
};
